import type { CreateAccountDetails } from '@/scopes/auth/domain/types'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Divider } from '@sunwebgroup/uds-ui/divider'
import { FormField } from '@sunwebgroup/uds-ui/fields/form-field'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from '@/intl'
import { useAuthActions, useAuthRecaptcha } from '@/scopes/auth/infrastructure/dependencies'
import { useAuthStore } from '@/scopes/auth/infrastructure/useAuthStore'
import { getIsSubmitDisabled } from '@/scopes/auth/pages/selectors/authSelectors'
import { CheckboxInput } from '@/scopes/common/components/FormElements/CheckboxInput'
import { EMAIL_INPUT_PATTERN } from '@/scopes/common/components/FormElements/patterns'
import { TextInput } from '@/scopes/common/components/FormElements/TextInput'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { AuthErrorBanner } from '../AuthErrorBanner'
import { AuthFormLayout } from '../AuthFormLayout'
import { NewPasswordInput } from '../password/NewPasswordInput'
import { CreateAccountConfirmation } from './CreateAccountConfirmation'
import styles from './CreateAccountForm.module.css'

const DEFAULT_FORM_VALUES: CreateAccountDetails = { email: '', newPassword: '', newsletterSun: false, newsletterSki: false }

export function CreateAccountForm() {
  const intl = useIntl()
  const methods = useForm<CreateAccountDetails>({ defaultValues: DEFAULT_FORM_VALUES })
  const { createAccountRequested, createAccountConfirmationDismissed, loginFormOpened } = useAuthActions()
  const submitStatus = useAuthStore(state => state.submitStatus)
  const hasSubmitError = useAuthStore(state => state.submitError !== null)
  const isSubmitDisabled = useAuthStore(getIsSubmitDisabled)
  const recaptchaRef = useAuthRecaptcha()
  const { conditions, privacy } = useConfiguration('links')

  async function handleSubmit(details: CreateAccountDetails) {
    const recaptchaToken = await recaptchaRef.current?.execute()

    return createAccountRequested({ ...details, recaptchaToken })
  }

  function handleUseDifferentEmail() {
    methods.reset(DEFAULT_FORM_VALUES)
    createAccountConfirmationDismissed()
  }

  const isLoadingStatus = submitStatus === 'loading'

  if (submitStatus === 'success') {
    return (
      <CreateAccountConfirmation
        email={methods.getValues('email')}
        onUseDifferentEmail={handleUseDifferentEmail}
      />
    )
  }

  return (
    <FormProvider {...methods}>
      <AuthFormLayout
        onSubmit={methods.handleSubmit(handleSubmit)}
        title={intl.formatMessage({ id: 'auth_create_account_form_title' })}
        subtitle={intl.formatMessage({ id: 'auth_create_account_form_subtitle' })}
        dataQa="auth-create-account-form"
      >
        <div className={styles.fields}>
          {hasSubmitError && <AuthErrorBanner message={intl.formatMessage({ id: 'auth_create_account_form_error_generic' })} />}
          <TextInput
            name="email"
            label={intl.formatMessage({ id: 'auth_form_email_label' })}
            helpText={intl.formatMessage({ id: 'auth_create_account_form_email_help_text' })}
            autoComplete="username"
            required
            requiredErrorMessage={intl.formatMessage({ id: 'auth_form_email_required_error' })}
            pattern={EMAIL_INPUT_PATTERN}
            patternErrorMessage={intl.formatMessage({ id: 'auth_form_email_pattern_error' })}
            disabled={isLoadingStatus}
          />
          <NewPasswordInput name="newPassword" disabled={isLoadingStatus} />
          <FormField
            label={intl.formatMessage({ id: 'auth_create_account_form_newsletters_label' })}
            optionalLabel={intl.formatMessage({ id: 'common_form_field_optional_label' })}
          >
            <div className={styles.newsletters}>
              <CheckboxInput
                name="newsletterSun"
                variant="outlined"
                label={intl.formatMessage({ id: 'auth_create_account_form_newsletter_sun_holidays_title' })}
                supportingText={intl.formatMessage({ id: 'auth_create_account_form_newsletter_sun_holidays_description' })}
                disabled={isLoadingStatus}
              />
              <CheckboxInput
                name="newsletterSki"
                variant="outlined"
                label={intl.formatMessage({ id: 'auth_create_account_form_newsletter_winter_sports_title' })}
                supportingText={intl.formatMessage({ id: 'auth_create_account_form_newsletter_winter_sports_description' })}
                disabled={isLoadingStatus}
              />
            </div>
          </FormField>
        </div>
        <div className={styles.footer}>
          <Button type="submit" variant="primary" fullWidth isLoading={isLoadingStatus} disabled={isSubmitDisabled}>
            {intl.formatMessage({ id: 'auth_create_account_form_submit_label' })}
          </Button>
          <Typography as="p" variant="body-s-regular-tight">
            {intl.formatMessage({ id: 'auth_create_account_form_has_account_label' })}
            {' '}
            <Typography as="button" type="button" variant="body-s-regular-tight" className={styles.link} onClick={loginFormOpened} disabled={isLoadingStatus}>
              {intl.formatMessage({ id: 'auth_sign_in_label' })}
            </Typography>
          </Typography>
          <Divider />
          <Typography as="p" variant="body-s-regular-tight" className={styles.legalText}>
            {intl.formatMessage(
              { id: 'auth_create_account_form_legal_text' },
              {
                termsLink: (chunks: (string | React.JSX.Element)[]) => <a href={conditions} target="_blank" rel="noopener noreferrer">{chunks}</a>,
                privacyLink: (chunks: (string | React.JSX.Element)[]) => <a href={privacy} target="_blank" rel="noopener noreferrer">{chunks}</a>,
              },
            )}
          </Typography>
        </div>
      </AuthFormLayout>
    </FormProvider>
  )
}
